import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector } from '@angular/core';
import { CardInfoModel, IPreapprovalCases } from '../models';
import { filter, map, mergeMap, Observable, of, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { StepReviewComponent } from "../step-review/step-review.component";
import { NavigationButtonsComponent } from "../../navigation-buttons/navigation-buttons.component";
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { PaymentsApiService, UnikoServicesConfig, UnikoServiceTypes } from 'src/app/services/payments-api.service';
import { InitiateWorkflowRequest, PortalApiService, PropertyDetails, PropertyListingDetails, PropertySummaryWithCountsResultSet } from 'src/app/services/portal-api.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { OrderResponse, OrderItemResponse, ServicesPaymentsApiService } from 'src/app/core/services/payments-api-service';
import { WorkflowApiService } from 'src/app/services/workflow.service';
import { HeaderService } from 'src/app/services/header.service';
import { RequestCallbackModalComponent } from '../../request-callback-modal/request-callback-modal.component';
import { ModalService, ToasterService, ToastType } from '@indice/ng-components';
import { IAsideModalResponse } from 'src/app/features/property-details/aside-content/aside-content.component';
import { SellingDetailsService } from 'src/app/features/my-profile/selling/selling-details/selling-details.service';
import { ExpertsCallbackComponent } from "../../experts-callback/experts-callback.component";
import { localizeRoute } from 'src/app/shared/utilities/localizeRoutes';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';

@Component({
  selector: 'app-checkout-result',
  standalone: true,
  templateUrl: './checkout-result.component.html',
  styleUrl: './checkout-result.component.css',
  imports: [CommonModule, StepReviewComponent, NavigationButtonsComponent, TranslocoDirective, ExpertsCallbackComponent, LanguageRouterLinkDirective]
})
export class CheckoutResultComponent {

  protected cardData: CardInfoModel | undefined;
  protected order$: Observable<OrderResponse>;
  protected order: OrderResponse | undefined;
  protected orderNumber: string = '';
  protected loading: boolean = false;
  protected isAobScreen: boolean = false;
  propertyId: string | undefined;
  protected propertyData: PropertyListingDetails | undefined;
  private productType?: UnikoServiceTypes
  protected propertyCode?: string;

  constructor(
    private _modalService: ModalService,
    private _paymentsApiService: ServicesPaymentsApiService,
    private activatedRoute: ActivatedRoute,
    private _router: Router,
    private _paymentsApi: PaymentsApiService,
    private _authService: AuthService,
    private _portalApiService: PortalApiService,
    private _workflowApiService: WorkflowApiService,
    private _headerService: HeaderService,
    private _toasterService: ToasterService,
    private _service: SellingDetailsService,
    private translocoService: TranslocoService,
    private envInjector: EnvironmentInjector
  ) {

    this.order$ = this.activatedRoute.params.pipe(
      tap(t => this.loading = true),
      switchMap((params: Params) => {
        this.orderNumber = params['orderNumber'];
        return this._paymentsApiService.getOrderByNumber(this.orderNumber);
      }),
      tap((response: OrderResponse) => {
        this.loading = false;
        this.order = response;
        const cardInfoJson = JSON.parse(response.transaction?.cardInfo ?? '{}');
        this.cardData = {
          number: cardInfoJson.number,
          nameOnCard: cardInfoJson.name,
          expiryDate: `${cardInfoJson.month}/${cardInfoJson.year}`,
        };
        this.order?.orderItems?.map((m: OrderItemResponse) => {
          this.productType = UnikoServicesConfig.find(f => f.sku == m.sku)?.type;
          switch (this.productType) {
            case UnikoServiceTypes.propertyUpload:
            case UnikoServiceTypes.unikoExclusive:
            case UnikoServiceTypes.unikoPhotography:
              this.isAobScreen = true;
              this._headerService.setAltNavigation(this.translocoService.translate('shared.checkout.checkout-result.aob-title.header'), undefined);
              break;
            default:
              this._headerService.setAltNavigation(this.translocoService.translate('shared.checkout.checkout-result.title.header'), undefined);
              break;
          }
        })
      })
    );

    this.order$.pipe(
      switchMap(result => this._portalApiService.getMySellingProperties(undefined)),
      map((properties: PropertySummaryWithCountsResultSet) => {
        const orderItem = this.order?.orderItems?.[0];
        if (!orderItem) return null;

        return properties.items?.find(p => {
          if (orderItem.propertyId === p.id) {
            this.propertyId = p.id;
          }
          return orderItem.propertyId === p.id
        })?.code;
      }),
      filter((code): code is string => code != null),
      switchMap((propertyCode) => {
        this.propertyCode = propertyCode;
        this._service.getListingDetails(propertyCode);
        return this._service.data$
      }),
      tap((listingDetails: PropertyDetails | undefined) => {
        this.propertyData = listingDetails;
      })
    ).subscribe({
      error: (error) => {
        console.error('Error fetching listing details:', error);
      }
    });

    // # Disable "Cancel Progress" validation when closing the wizard on the final step (eg checkout/summary)
    this._headerService.showCancelProgress(false);
  }

  public onCallbackClick() {
    const currentUser = this._authService.getCurrentUser();
    const modal = this._modalService.show(RequestCallbackModalComponent, {
      keyboard: true,
      class: 'modal-xl',
      initialState: {
        property: new PropertyDetails({
          address: this.propertyData?.basicInfo?.address,
          propertyArea: this.propertyData?.basicInfo?.area,
          type: this.propertyData?.basicInfo?.type,
          id: this.propertyData?.propertyId
        }),
        isLoggedIn: !!currentUser,
      },
    });
    modal.onHidden?.pipe(
      tap((t: any) => {
        let input = t.result as IAsideModalResponse;
        if (input.success != undefined) {
          if (input.success == true) {

            const callbackRequestSent = this.translocoService.translate('checkoutcallbackRequestSent');
            const message = this.translocoService.translate('callbackAdvisorMessage', {
              advisorName: this.propertyData?.advisor?.fullName ?? '',
              phoneNumber: currentUser?.profile?.phone_number ?? ''
            });
            this._toasterService.show(ToastType.Success, callbackRequestSent, message, 10000);
          } else {

            const callbackRequestError = this.translocoService.translate('callbackRequestError');
            this._toasterService.show(ToastType.Error, callbackRequestError, input.errorDesc, 10000);
          }
        }
      })
    ).subscribe();
  }

  protected goToListing() {
    if (this.propertyCode)
      this._router.navigate([localizeRoute(`/my-profile/listings/${this.propertyCode}`, this.envInjector)])
  }
  protected goToBrokerage() {
    if (this.propertyCode)
      this._router.navigate([localizeRoute(`/my-profile/listings/${this.propertyCode}/brokerage-agreement`, this.envInjector)], {
        queryParams: {
          isAob: true
        }
      });
  }
  protected onNextClick(): void {
    // # Reactivate "Cancel Progress" validation when closing the wizard, to restore the original logic for the next service purchase flow.
    this._router.navigate([localizeRoute(`/my-profile/requests`, this.envInjector)])
      .then((_) => this._headerService.showCancelProgress(true));

  }
}
